import { useState } from 'react';
import { Box, Group, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons';

interface JsonTreeProps {
  value: unknown;
  /** Depth up to which branches start expanded. */
  defaultExpandedDepth?: number;
}

type JsonKind = 'string' | 'number' | 'boolean' | 'null' | 'undefined' | 'array' | 'object';

const INDENT = 14;
const MONO = { fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 } as const;

function kindOf(value: unknown): JsonKind {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return 'array';
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return t;
  return 'object';
}

function primitiveColor(kind: JsonKind): string {
  if (kind === 'string') return 'teal';
  if (kind === 'number') return 'blue';
  if (kind === 'boolean') return 'grape';
  return 'dimmed';
}

function primitiveText(value: unknown, kind: JsonKind): string {
  if (kind === 'string') return `"${value as string}"`;
  if (kind === 'null') return 'null';
  if (kind === 'undefined') return 'undefined';
  return String(value);
}

function entriesOf(value: unknown, kind: JsonKind): Array<[string, unknown]> {
  if (kind === 'array') return (value as unknown[]).map((item, index) => [String(index), item]);
  return Object.entries(value as Record<string, unknown>);
}

function KeyLabel({ name }: { name: string }) {
  return (
    <Text span sx={{ ...MONO, color: '#5c5f66' }}>
      {name}:
    </Text>
  );
}

function PrimitiveValue({ value }: { value: unknown }) {
  const kind = kindOf(value);
  return (
    <Text
      span
      color={primitiveColor(kind)}
      sx={{ ...MONO, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
    >
      {primitiveText(value, kind)}
    </Text>
  );
}

function BranchNode({
  name,
  value,
  kind,
  depth,
  defaultExpandedDepth,
}: {
  name?: string;
  value: unknown;
  kind: JsonKind;
  depth: number;
  defaultExpandedDepth: number;
}) {
  const [expanded, setExpanded] = useState(depth < defaultExpandedDepth);
  const entries = entriesOf(value, kind);
  const open = kind === 'array' ? '[' : '{';
  const close = kind === 'array' ? ']' : '}';
  const unit = kind === 'array' ? 'item' : 'key';
  const summary = `${entries.length} ${unit}${entries.length === 1 ? '' : 's'}`;

  return (
    <Box>
      <Group
        spacing={4}
        noWrap
        align="center"
        onClick={() => setExpanded((prev) => !prev)}
        sx={(theme) => ({
          cursor: 'pointer',
          borderRadius: theme.radius.sm,
          '&:hover': { backgroundColor: theme.colors.gray[0] },
        })}
      >
        <IconChevronRight
          size={12}
          style={{
            flexShrink: 0,
            transition: 'transform 120ms ease',
            transform: expanded ? 'rotate(90deg)' : 'none',
            color: '#adb5bd',
          }}
        />
        {name !== undefined ? <KeyLabel name={name} /> : null}
        <Text span sx={{ ...MONO, color: '#868e96' }}>
          {expanded ? open : `${open} ${summary} ${close}`}
        </Text>
      </Group>
      {expanded ? (
        <Box
          sx={(theme) => ({
            marginLeft: 6,
            paddingLeft: INDENT,
            borderLeft: `1px solid ${theme.colors.gray[2]}`,
          })}
        >
          {entries.map(([childName, childValue]) => (
            <JsonNode
              key={childName}
              name={childName}
              value={childValue}
              depth={depth + 1}
              defaultExpandedDepth={defaultExpandedDepth}
            />
          ))}
          <Text span sx={{ ...MONO, color: '#868e96' }}>
            {close}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

function JsonNode({
  name,
  value,
  depth,
  defaultExpandedDepth,
}: {
  name?: string;
  value: unknown;
  depth: number;
  defaultExpandedDepth: number;
}) {
  const kind = kindOf(value);
  const entries = kind === 'array' || kind === 'object' ? entriesOf(value, kind) : [];
  const isEmptyBranch = (kind === 'array' || kind === 'object') && entries.length === 0;

  return kind === 'array' || kind === 'object' ? (
    isEmptyBranch ? (
      <Group spacing={4} noWrap sx={{ paddingLeft: 16 }}>
        {name !== undefined ? <KeyLabel name={name} /> : null}
        <Text span sx={{ ...MONO, color: '#868e96' }}>
          {kind === 'array' ? '[ ]' : '{ }'}
        </Text>
      </Group>
    ) : (
      <BranchNode
        name={name}
        value={value}
        kind={kind}
        depth={depth}
        defaultExpandedDepth={defaultExpandedDepth}
      />
    )
  ) : (
    <Group spacing={4} noWrap={false} align="flex-start" sx={{ paddingLeft: 16 }}>
      {name !== undefined ? <KeyLabel name={name} /> : null}
      <PrimitiveValue value={value} />
    </Group>
  );
}

export function JsonTree({ value, defaultExpandedDepth = 1 }: JsonTreeProps) {
  return (
    <Box sx={{ maxWidth: '100%' }}>
      <JsonNode value={value} depth={0} defaultExpandedDepth={defaultExpandedDepth} />
    </Box>
  );
}
